// Phase 35 (C4 / D5) — Settings → Document billing: per-mode activity allowlist.
//
// Three columns (Read / Review / Write). Each is a checkbox list over every
// activity-code taxonomy entry; the checked codes are the activities the
// DocumentBillingModal will offer for a document set to that mode. The allowlist
// is stored on FirmSettings.docBillingModeActivities
// ({ read:[code…], review:[code…], write:[code…] }); a mode with NO checked
// codes = no restriction (all activities shown). Admin-only — SettingsView
// mounts this behind the admin gate, and PUT /firm-settings additionally
// enforces it server-side (ADMIN_ONLY_FIELD).
//
// Self-contained component (stable identity, not defined inside a render body)
// per CLAUDE.md "Known patterns".

import React, { useState, useEffect } from "react"; // React: react/react-in-jsx-scope counts every JSX element (the house remedy; 211 §6)
import { api } from "../api";
import { useTranslation } from "../i18n";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { RAD, FS } from "../lib/tokens";
import { pickEntryLabel } from "../utils/taxonomyMap"; // 211 §6 — every activity's name in the reader's language
import type { TaxEntry } from "../lib/taxonomyTypes";

/* 211 §6 [Laurent: « excorr always showed up by default »] — THE ORDER. A mode's
   ticked activities are listed first, in the order the stored list keeps them,
   each movable up and down; the billing window and the attachment window offer
   them in that order, the first being the mode's default (src/lib/docBillingRow.ts
   `allowedActs`, `defaultAct`). A newly ticked activity joins the end. The
   unticked follow, in the taxonomy's order. Every name goes through
   `pickEntryLabel` — « Étude du dossier » on a French screen, never the stored
   English label. */
/** A mode's ticked activities in their stored order, then the rest in the taxonomy's. */
function orderedActs(entries: TaxEntry[], ticked: readonly string[]): { ticked: TaxEntry[]; rest: TaxEntry[] } {
  const byCode = new Map(entries.map((a) => [a.code, a] as const));
  return {
    ticked: ticked.map((c) => byCode.get(c)).filter((a): a is TaxEntry => a !== undefined),
    rest: entries.filter((a) => !ticked.includes(a.code ?? "")),
  };
}
/** The list with `code` moved one place up (-1) or down (+1). */
function moved(list: readonly string[], code: string, by: number): string[] {
  const i = list.indexOf(code);
  const j = i + by;
  if (i < 0 || j < 0 || j >= list.length) return [...list];
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

const MODES = [
  { key: "read", fallback: "Read" },
  { key: "review", fallback: "Review" },
  { key: "write", fallback: "Write" },
];

type DocBillingActivitiesPanelProps = {
  isAdmin?: boolean;
  toast?: (msg: string, kind?: string) => void;
};

export default function DocBillingActivitiesPanel({ isAdmin, toast }: DocBillingActivitiesPanelProps) {
  const { t } = useTranslation();
  const { entries: activityEntries } = useTaxonomy("activity_code");
  const [cfg, setCfg] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<{ docBillingModeActivities?: { read?: unknown; review?: unknown; write?: unknown } | null }>("/firm-settings")
      .then((d) => {
        if (cancelled) return;
        const v: { read?: unknown; review?: unknown; write?: unknown } = d?.docBillingModeActivities || {};
        setCfg({
          read: Array.isArray(v.read) ? v.read : [],
          review: Array.isArray(v.review) ? v.review : [],
          write: Array.isArray(v.write) ? v.write : [],
        });
      })
      .catch(() => {
        if (!cancelled) setCfg({ read: [], review: [], write: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 211 §6 — one place up or down in the mode's stored order
  const move = (mode: string, code: string, by: number) => {
    setCfg((p: any) => ({ ...p, [mode]: moved((p[mode] || []) as string[], code, by) }));
  };
  const toggle = (mode: string, code: string) => {
    setCfg((p: any) => {
      const cur = new Set(p[mode] || []);
      if (cur.has(code)) cur.delete(code);
      else cur.add(code);
      return { ...p, [mode]: [...cur] };
    });
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api("/firm-settings", {
        method: "PUT",
        body: JSON.stringify({ docBillingModeActivities: cfg }),
      });
      toast?.(
        t("settings.docActs.saved", {
          fallback: "Document billing activities saved",
        }),
      );
    } catch (e) {
      toast?.("⚠ " + ((e as any).message || t("common.save_failed", { fallback: "Save failed" })), "error");
    }
    setSaving(false);
  };

  return (
    <div className="detail-card settings-pane" style={{ order: 75 }}>
      <h4
        className="settings-pane-title"
        style={{ margin: 0, cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="settings-chevron">{open ? "▼" : "▶"}</span>
        {t("settings.docActs.title", {
          fallback: "Document billing — activities per mode",
        })}
      </h4>
      {open && (
        <>
          <p
            style={{
              fontSize: FS.s11_5,
              color: "var(--ts)",
              margin: "6px 0 10px",
              lineHeight: 1.6,
            }}
          >
            {t("settings.docActs.desc", {
              fallback:
                "Choose which activity codes appear in the document-billing dialog for each mode. A mode with nothing ticked offers all activities.",
            })}{" "}
            {t("settings.docActs.order_hint", {
              fallback: "The ticked activities are offered in this order; the first is the mode's default.",
            })}
          </p>
          {!cfg ? (
            <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
              {t("settings.label.loading", { fallback: "Loading…" })}
            </p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
                  gap: 14,
                }}
              >
                {MODES.map((m) => (
                  <div
                    key={m.key}
                    style={{
                      border: "1px solid var(--br)",
                      borderRadius: RAD.r6,
                      padding: "8px 10px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s11,
                        fontWeight: 600,
                        color: "var(--tp)",
                        marginBottom: 6,
                      }}
                    >
                      {t(`docBilling.mode_${m.key}`, { fallback: m.fallback })}{" "}
                      <span style={{ color: "var(--tm)", fontWeight: 400 }}>
                        ({(cfg[m.key] || []).length || t("common.all", { fallback: "all" })})
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        maxHeight: 240,
                        overflowY: "auto",
                      }}
                    >
                      {(() => {
                        const { ticked, rest } = orderedActs(activityEntries as TaxEntry[], (cfg[m.key] || []) as string[]);
                        const row = (a: TaxEntry, on: boolean, i: number) => (
                          <label
                            key={a.code}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: FS.s10_5,
                              cursor: isAdmin ? "pointer" : "default",
                              color: "var(--ts)",
                            }}
                            title={`${a.code ?? ""} — ${pickEntryLabel(a)}`}
                          >
                            <input
                              type="checkbox"
                              disabled={!isAdmin}
                              checked={on}
                              onChange={() => {
                                toggle(m.key, a.code ?? "");
                              }}
                            />
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <code style={{ color: "var(--ac)" }}>{a.code}</code>{" "}
                              {pickEntryLabel(a)}
                            </span>
                            {on && isAdmin && (
                              <span style={{ display: "inline-flex", gap: 2 }}>
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  disabled={i === 0}
                                  title={t("settings.docActs.move_up", { fallback: "Move up" })}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    move(m.key, a.code ?? "", -1);
                                  }}
                                  style={{ padding: "0 5px", fontSize: FS.s10, opacity: i === 0 ? 0.35 : 1 }}
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  disabled={i === ticked.length - 1}
                                  title={t("settings.docActs.move_down", { fallback: "Move down" })}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    move(m.key, a.code ?? "", 1);
                                  }}
                                  style={{ padding: "0 5px", fontSize: FS.s10, opacity: i === ticked.length - 1 ? 0.35 : 1 }}
                                >
                                  ▼
                                </button>
                              </span>
                            )}
                          </label>
                        );
                        return (
                          <>
                            {ticked.map((a, i) => row(a, true, i))}
                            {ticked.length > 0 && rest.length > 0 && <hr style={{ border: 0, borderTop: "1px solid var(--br)", margin: "4px 0" }} />}
                            {rest.map((a, i) => row(a, false, i))}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
              {isAdmin && (
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 12 }}
                  disabled={saving}
                  onClick={save}
                >
                  {saving
                    ? t("settings.btn.saving", { fallback: "Saving…" })
                    : t("settings.btn.save", { fallback: "Save" })}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
